import React, { createContext, useContext, useEffect, useState } from 'react';
import { API_BASE } from '../../services/api';
import { setCurrentCaseStorageUser, migrateLegacyCasesIfNeeded } from '../caseStorage';

export type UserRole = 'consumer' | 'pro' | 'admin';

export interface AuthUser {
    id: string;
    email: string;
    emailVerified?: boolean;
    hasPaidReport?: boolean;
    canCreateEstate?: boolean;
    /**
     * Server-assigned role. Defaults to 'consumer' for new accounts.
     * Pro/admin users get access to the /pro workspace; consumers do not.
     * Old /me responses without a role field are coerced to 'consumer'
     * via the AuthProvider so existing sessions don't break on rollout.
     */
    role: UserRole;
}

interface AuthContextType {
    user: AuthUser | null;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Keep case storage scoped to the current user so accounts on the same
    // browser cannot see each other's locally cached cases.
    useEffect(() => {
        setCurrentCaseStorageUser(user?.id ?? null);
        if (user?.id) migrateLegacyCasesIfNeeded();
    }, [user?.id]);

    // Restore session on mount
    useEffect(() => {
        fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then((data: any) => setUser(data?.user ? { ...data.user, role: data.user.role ?? 'consumer' } : null))
            .catch(() => setUser(null))
            .finally(() => setIsLoading(false));
    }, []);

    const login = async (email: string, password: string) => {
        const r = await fetch(`${API_BASE}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, password }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Login failed');
        setUser(data.user ? { ...data.user, role: data.user.role ?? 'consumer' } : null);
    };

    const register = async (email: string, password: string) => {
        const r = await fetch(`${API_BASE}/api/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, password }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Registration failed');
        setUser(data.user ? { ...data.user, role: data.user.role ?? 'consumer' } : null);
    };

    const logout = async () => {
        await fetch(`${API_BASE}/api/auth/logout`, { method: 'POST', credentials: 'include' });
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, isLoading, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextType {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
